import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./popover.html";
import { getFocusTrap } from '~/modules/helpers/focusTrap';
import { selectSharedLabels, WireI18n } from '~/modules/stores/i18nStore';
import { handleScrollLock } from '~/modules/utilities/browser';
export let PopoverVerticalOrientation = /*#__PURE__*/function (PopoverVerticalOrientation) {
  PopoverVerticalOrientation["TOP"] = "top";
  PopoverVerticalOrientation["BOTTOM"] = "bottom";
  return PopoverVerticalOrientation;
}({});
export let PopoverHorizontalOrientation = /*#__PURE__*/function (PopoverHorizontalOrientation) {
  PopoverHorizontalOrientation["RIGHT"] = "right";
  PopoverHorizontalOrientation["LEFT"] = "left";
  PopoverHorizontalOrientation["CENTER"] = "center";
  return PopoverHorizontalOrientation;
}({});
const VIEWPORT_PADDING = 16;
class Popover extends LightningElement {
  constructor(...args) {
    super(...args);
    this.show = false;
    this.ariaLabel = null;
    this.hideCloseButton = false;
    /**
     * When true, open above the trigger if the panel would not fit under it
     * (sticky listing CTAs count as occupied). Off by default so sort/help
     * popovers keep their requested orientation.
     */
    this.flipIfOverflowsViewport = false;
    this.focusContainerOnOpen = false;
    this.popoverOrientation = {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.LEFT
    };
    this.ariaLabelledBy = null;
    this.labels = void 0;
    this.rafId = null;
    this.wasShowing = false;
    this.flipToTop = false;
    // If a popover is showing, check that the click on document.
    // Body is happening outside of the popover. If it is, close the popover.
    this.createCatchClickHandler = async e => {
      if (this.show) {
        const composedPath = e.composedPath();
        // @ts-expect-error * Automated Type Error Fix
        const externalClick = !composedPath.includes(this.template.querySelector('.popover-wrapper'));
        // @ts-expect-error * Automated Type Error Fix
        const toolTipClick = composedPath.includes(this.template.querySelector('.popover-trigger'));
        if (externalClick && !toolTipClick) {
          // Returns the scroll position to the same position after the this.handleClose()
          handleScrollLock(() => {
            this.handleClose();
          });
        }
      }
    };
  }
  connectedCallback() {
    document.addEventListener('mousedown', this.createCatchClickHandler);
  }
  disconnectedCallback() {
    document.removeEventListener('mousedown', this.createCatchClickHandler);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.wasShowing = false;
  }
  renderedCallback() {
    if (!this.ariaLabel) {
      // @ts-expect-error * Automated Type Error Fix
      this.ariaLabelledBy = 'popover-content';
    }
    if (this.show) {
      const slotElement = this.template.querySelector('.popover');
      if (!slotElement) {
        return;
      }
      this.focusTrap = getFocusTrap(slotElement);
      if (!this.wasShowing) {
        if (this.focusContainerOnOpen) {
          slotElement.focus();
        } else {
          this.focusTrap.focusFirstElement();
        }
        this.wasShowing = true;
      }
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.placeInViewport(slotElement);
      });
    } else {
      this.wasShowing = false;
      this.flipToTop = false;
    }
  }
  placeInViewport(popoverEl) {
    const hostRect = this.template.host.getBoundingClientRect();
    const panelHeight = popoverEl.offsetHeight || popoverEl.scrollHeight || popoverEl.getBoundingClientRect().height;
    const viewH = window.visualViewport?.height ?? window.innerHeight;
    const spaceBelow = viewH - VIEWPORT_PADDING - hostRect.bottom;
    const requestedTop = this.popoverOrientation?.vertical === PopoverVerticalOrientation.TOP;
    const doesNotFitBelow = panelHeight > Math.max(0, spaceBelow);
    if (this.flipIfOverflowsViewport && !requestedTop && !this.flipToTop && doesNotFitBelow) {
      this.flipToTop = true;
      popoverEl.classList.remove('vertical-bottom');
      popoverEl.classList.add('vertical-top');
      const arrowEl = this.template.querySelector('.arrow');
      arrowEl?.classList.remove('vertical-bottom');
      arrowEl?.classList.add('vertical-top');
    }
    this.clampToViewport(popoverEl);
  }
  clampToViewport(popoverEl) {
    popoverEl.style.removeProperty('left');
    popoverEl.style.removeProperty('right');
    popoverEl.style.removeProperty('max-width');
    popoverEl.style.removeProperty('transform');
    const arrowEl = this.template.querySelector('.arrow');
    if (arrowEl) {
      arrowEl.style.removeProperty('left');
      arrowEl.style.removeProperty('right');
    }
    const rect = popoverEl.getBoundingClientRect();
    const padding = 16;
    const viewportWidth = window.innerWidth;
    if (rect.right <= viewportWidth - padding && rect.left >= padding) {
      return;
    }
    const positioningRect = popoverEl.offsetParent?.getBoundingClientRect() ?? this.template.host.getBoundingClientRect();
    const availableWidth = viewportWidth - padding * 2;
    const arrowLeft = arrowEl?.offsetLeft ?? 0;
    popoverEl.style.maxWidth = `${availableWidth}px`;
    const popoverWidth = Math.min(popoverEl.getBoundingClientRect().width, availableWidth);
    const viewportLeft = Math.max(padding, Math.min(rect.left, viewportWidth - padding - popoverWidth));
    const popoverLeftRelativeToPositioningElement = viewportLeft - positioningRect.left;
    popoverEl.style.left = `${popoverLeftRelativeToPositioningElement}px`;
    popoverEl.style.right = 'auto';
    popoverEl.style.transform = 'none';
    if (arrowEl) {
      const arrowWidth = arrowEl.offsetWidth;
      const borderRadius = 8;
      const minArrowLeft = popoverLeftRelativeToPositioningElement + borderRadius;
      const maxArrowLeft = popoverLeftRelativeToPositioningElement + popoverWidth - borderRadius - arrowWidth;
      const clampedArrowLeft = Math.max(minArrowLeft, Math.min(arrowLeft, maxArrowLeft));
      arrowEl.style.left = `${clampedArrowLeft}px`;
      arrowEl.style.right = 'auto';
    }
  }
  get popoverClass() {
    const vertical = this.flipToTop ? PopoverVerticalOrientation.TOP : this.popoverOrientation?.vertical ?? PopoverVerticalOrientation.BOTTOM;
    const horizontal = this.popoverOrientation?.horizontal ?? PopoverHorizontalOrientation.LEFT;
    return `popover vertical-${vertical} horizontal-${horizontal}`;
  }
  get arrowClass() {
    const vertical = this.flipToTop ? PopoverVerticalOrientation.TOP : this.popoverOrientation?.vertical ?? PopoverVerticalOrientation.BOTTOM;
    const horizontal = this.popoverOrientation?.horizontal ?? PopoverHorizontalOrientation.LEFT;
    return `arrow vertical-${vertical} horizontal-${horizontal}`;
  }
  get contentClass() {
    return this.hideCloseButton ? 'content' : 'close-button-content';
  }
  get closeButtonAriaLabel() {
    return this.labels?.close ?? '';
  }
  handleClose() {
    this.dispatchEvent(new CustomEvent('close'));
  }

  // @ts-expect-error * Automated Type Error Fix
  handleInputKey(event) {
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        this.handlePrevFocus(event);
      } else {
        this.handleNextFocus(event);
      }
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleNextFocus(event) {
    const {
      focusNext
    } = this.focusTrap;
    const {
      shouldPreventDefault
    } = focusNext();
    if (shouldPreventDefault) {
      event.preventDefault();
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handlePrevFocus(event) {
    const {
      focusPrevious
    } = this.focusTrap;
    const {
      shouldPreventDefault
    } = focusPrevious();
    if (shouldPreventDefault) {
      event.preventDefault();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Popover, {
  publicProps: {
    show: {
      config: 0
    },
    ariaLabel: {
      config: 0
    },
    hideCloseButton: {
      config: 0
    },
    flipIfOverflowsViewport: {
      config: 0
    },
    focusContainerOnOpen: {
      config: 0
    },
    popoverOrientation: {
      config: 0
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectSharedLabels
        };
      }
    }
  },
  fields: ["ariaLabelledBy", "rafId", "wasShowing", "flipToTop", "createCatchClickHandler"]
});
const __lwc_component_class_internal = _registerComponent(Popover, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;